import React, { useRef, useState } from 'react';
import ResourceView from './ResourceTab/ResourceView';
import { useUscgMapContext } from './LayersTab/UscgMapContext';
import ResourceRequestView from './ResourceTab/ResourceRequestView';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { useIsDev } from 'hooks/useIsDev';
import { useLocation } from 'react-router';
import { useAppSelector, selectIncident } from 'slices/commonSelectors';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  useCheckInResource,
  useUpdateResourceCheckIn,
  useUpdateResourceStatus,
  useCheckOutResource,
  ResourceInIncident,
} from 'components/IAP/hooks/resourceInIncidentHooks';
import { CheckedInResources } from './ResourceTab/CheckedInResources';
import { CheckedOutResources } from './ResourceTab/CheckedOutResources';
import { useForceFetchExternalData } from '@/components/IAP/hooks/resourceSetHooks';
import { useImportAlmisDataFromFile } from '@/hooks/useImportAlmisDataFromFile';
import { noAutoClose } from '@/assets/data/config';
import { toast } from 'react-toastify';
import { parseError } from '@/hooks/shared/error';

type ResourcesProps = {
  forceFullView?: boolean;
};

const Resources: React.FC<ResourcesProps> = ({ forceFullView }) => {
  const [selectedTab, setSelectedTab] = useState('resource');
  const [showAddResourceCard, setShowAddResourceCard] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const { isMapVisible } = useUscgMapContext();
  const isDev = useIsDev();
  const { mutate: importAlmisDataFromFile } = useImportAlmisDataFromFile();
  const currentIncident = useAppSelector(selectIncident);
  const incidentId = currentIncident?.id;
  const location = useLocation();
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const { mutate: forceFetchExternalData } = useForceFetchExternalData();
  const exportAsForm = icsFormPdf?.exportAsForm;
  const toggleData = [
    { label: 'Resource', value: 'resource' },
    { label: 'Resource Request', value: 'request' },
  ];

  const hasCheckInFields = (data: Partial<ResourceInIncident>): boolean => {
    const checkInFields = [
      'leader_name',
      'personnel_count',
      'order_request_number',
      'state_and_agency',
      'resource_category',
      'resource_kind',
      'resource_type',
      'home_unit_agency',
      'departure_point',
      'departure_datetime',
      'travel_method',
    ];
    return checkInFields.some((field) => field in data);
  };

  const checkInMutation = useCheckInResource();
  const updateCheckInMutation = useUpdateResourceCheckIn();
  const updateStatusMutation = useUpdateResourceStatus();
  const checkOutMutation = useCheckOutResource();

  // Handle resource updates
  const handleResourceUpdate = async (
    updatedResource: Partial<ResourceInIncident>
  ) => {
    // Determine update type based on the data
    if (updatedResource.checkin_datetime && !updatedResource.id) {
      // This is a new check-in
      await checkInMutation.mutateAsync(updatedResource);
    } else if (
      updatedResource.checkout_datetime &&
      updatedResource.checkout_datetime !== null &&
      updatedResource.id
    ) {
      // This is a check-out (inferred from data)
      await checkOutMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    } else if (updatedResource.id && hasCheckInFields(updatedResource)) {
      // This is a check-in update (has check-in specific fields)
      await updateCheckInMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    } else if (updatedResource.id) {
      // This is a status update
      await updateStatusMutation.mutateAsync({
        id: updatedResource.id,
        data: updatedResource,
      });
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <div className="resource-buttons-row resource-header">
        <div className="d-flex align-items-center gap-2">
          <StylishNewToggleButtonGroup
            data={toggleData}
            selected={selectedTab}
            onChange={(value) => {
              if (
                value === 'request' &&
                location.pathname !== `/iap/${incidentId}/resources`
              ) {
                setShowModal(true);
              } else {
                setSelectedTab(value);
              }
            }}
            className="resource-button-group"
          />
          {(location.pathname === `/iap/${incidentId}/resources` ||
            forceFullView) && (
            <>
              <StylishNewButton
                className={
                  selectedTab === 'checked-in' ? '' : 'button--secondary'
                }
                onClick={() => setSelectedTab('checked-in')}
              >
                ICS-211
              </StylishNewButton>
              <StylishNewButton
                className={
                  selectedTab === 'checked-out' ? '' : 'button--secondary'
                }
                onClick={() => setSelectedTab('checked-out')}
              >
                ICS-238
              </StylishNewButton>
            </>
          )}
          {selectedTab !== 'resource' && (
            <StylishNewSearchBar
              onChangeSearchTerm={(term) => setSearch(term)}
            />
          )}
        </div>
        {selectedTab === 'checked-in' ? (
          exportAsForm && (
            <StylishNewButton
              className="button--secondary"
              onClick={() =>
                void exportAsForm({ formname: 'ICS_211_CG', mapSketch: null })
              }
            >
              Preview ICS-211
            </StylishNewButton>
          )
        ) : selectedTab === 'checked-out' ? (
          exportAsForm && (
            <StylishNewButton
              className="button--secondary"
              onClick={() =>
                void exportAsForm({ formname: 'ICS_238_CG', mapSketch: null })
              }
            >
              Preview ICS-238
            </StylishNewButton>
          )
        ) : selectedTab === 'resource' ? (
          <div className="d-flex align-items-center gap-2">
            <StylishNewButton
              className="button--secondary"
              onClick={() => forceFetchExternalData()}
            >
              Refresh External Resources
            </StylishNewButton>
            {isDev && (
              <input
                ref={fileInputRef}
                id="cg-geojson-upload"
                accept="application/json,.json"
                aria-label="Upload ALMIS data file"
                type="file"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (!files.length) return;

                  // Reset so the same file(s) can be re-selected if needed
                  e.target.value = '';
                  importAlmisDataFromFile(files[0], {
                    onSuccess: () => {
                      toast.success('ALMIS data upload succeeded');
                    },
                    onError: (error) => {
                      const message =
                        parseError(error) ?? 'ALMIS data upload failed.';
                      toast.error(message, noAutoClose);
                    },
                  });
                }}
                tabIndex={-1}
              />
            )}
            <StylishNewButton
              className="button--secondary"
              onClick={() => setShowAddResourceCard(!showAddResourceCard)}
            >
              Add Resource
            </StylishNewButton>
          </div>
        ) : selectedTab === 'request' ? (
          <StylishNewButton
            className="button--secondary"
            onClick={() => setShowAddResourceCard(!showAddResourceCard)}
          >
            Add Resource Request
          </StylishNewButton>
        ) : null}
      </div>
      {selectedTab === 'resource' ? (
        <ResourceView
          showAddResourceCard={showAddResourceCard}
          setShowAddResourceCard={setShowAddResourceCard}
          handleResourceUpdate={handleResourceUpdate}
          searchTerm={search}
          compact={
            !forceFullView &&
            isMapVisible &&
            location.pathname !== `/iap/${incidentId}/resources`
          }
        />
      ) : selectedTab === 'checked-in' ? (
        <CheckedInResources
          handleResourceUpdate={handleResourceUpdate}
          searchTerm={search}
        />
      ) : selectedTab === 'checked-out' ? (
        <CheckedOutResources
          handleResourceUpdate={handleResourceUpdate}
          searchTerm={search}
        />
      ) : (
        <ResourceRequestView
          showAddResourceCard={showAddResourceCard}
          setShowAddResourceCard={setShowAddResourceCard}
          searchTerm={search}
        />
      )}
      {showModal && (
        <CustomModal
          heading="Cannot View Resource Requests"
          onClose={() => setShowModal(false)}
          show={showModal}
          onConfirm={() => setShowModal(false)}
          saveButton="OK"
        >
          <p>Resource Requests cannot be viewed here</p>
        </CustomModal>
      )}
    </>
  );
};

export default Resources;
